import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import supabase from '../supabase.js';
import { useAuth } from '../context/AuthContext.jsx';

// Assets
import searchbaricon from '../assets/searchbarico.svg';
import finalDARkIcon from '../assets/FINALDARKLOGO.svg';
import analyticsSvg from '../mainpages/businesspages/businessassets/analyticsbusiness.svg'
import homesvg from '../mainpages/businesspages/businessassets/homebusiness.svg'
import settingsSvg from '../mainpages/businesspages/businessassets/settingsbusiness.svg'
import widgetsvg from '../mainpages/businesspages/businessassets/widgetbusiness.svg'
import yourpagesvg from '../mainpages/businesspages/businessassets/yourpagebusiness.svg'
import pricingsvg from '../mainpages/businesspages/businessassets/dollar.svg'
import faqssvg from '../mainpages/businesspages/businessassets/questionSvg.svg'
import intelsvg from '../mainpages/businesspages/businessassets/intel.svg'
import FullPageLoader from './FullPageLoader.jsx';
// Inside your Header component:

import '../app.css';

export default function Header({ selectedLanguage, handleLanguageChange, flagIcon }) {
  const navigate = useNavigate();
  const { user, userRow, business, isAdmin, isBusiness, loading } = useAuth();
  const { pathname } = useLocation()
  const [isOpen, setIsOpen] = useState(false);
  const [isSearch, setIsSearch] = useState(false);

// 1. Add this state at the top of your component (Sidebar or Header)
const [isLoggingOut, setIsLoggingOut] = useState(false);

const handleLogout = async () => {
  setIsOpen(false); 
  setIsLoggingOut(true); // 2. Immediately trigger the global loader
  
  // Clear storage first
  localStorage.clear(); 
  sessionStorage.clear();

  try {
    // 3. Sign out. While this "waits," the loader is visible.
    await supabase.auth.signOut();
  } catch (err) {
    console.error("Logout error:", err);
  } finally {
    // 4. Force a hard refresh to the home page
    window.location.href = '/';
  }
};

  const fullname = useMemo(() => {
    if (loading) return "";

    // Priority 1: Data from your 'users' or 'profiles' table (userRow)
    if (userRow?.first_name) return `${userRow.first_name} ${userRow.last_name || ''}`.trim();
    if (userRow?.name) return `${userRow.name} ${userRow.lastname || ''}`.trim();

    // Priority 2: Data from the Auth Metadata (What we just fixed in SignUp)
    const meta = user?.user_metadata;
    if (meta?.display_name) return meta.display_name;
    if (meta?.full_name) return meta.full_name;
    if (meta?.first_name) return `${meta.first_name} ${meta.last_name || ''}`.trim();
    
    // Priority 3: Legacy metadata keys
    if (meta?.name) return `${meta.name} ${meta.lastname || ''}`.trim();

    // Priority 4: If no name exists, use the beginning of the email
    if (user?.email) return user.email.split('@')[0];

    return 'User';
  }, [user, userRow, loading]);

  const getColorForName = (name) => {
    if (!name || name === 'User') return '#1E7D6B';
    const COLORS = ['#2F855A', '#D69E2E', '#805AD5', '#DD6B20', '#3182CE', '#1E7D6B'];
    const sum = name.split('').reduce((s, char) => s + char.charCodeAt(0), 0);
    return COLORS[sum % COLORS.length];
  };

  const avatarBg = useMemo(() => getColorForName(fullname), [fullname]);

  const translations = {
    english: { signIn: 'Log in', signUp: 'Sign up' },
    german: { signIn: 'Einloggen', signUp: 'Registrieren' },
  };

  if (isLoggingOut) {
  return <FullPageLoader />; // Or whatever your loader component is called
}

const isSubBusinessPage = 
  (pathname === '/' || pathname.startsWith('/business/')) && 
  pathname !== '/business' && 
  pathname !== '/business/signup';


  return (
    <header className={`fixed top-0 z-50 w-full pt-[15px] pointer-events-none transition-all duration-300 ${isSubBusinessPage && isBusiness ? 'lg:w-[calc(100%-32px)]  xs:px-[32px] px-[12px]  lg:mx-[16px] lg:mx-auto lg:pr-[250px] xl:pr-[350px]' : ''}`}>
      
      <div className={`absolute top-0 w-full h-[120%] bg-white/10 backdrop-blur-md  -z-10 [mask-image:linear-gradient(to_bottom,black_60%,transparent)] ${isSubBusinessPage && isBusiness ?'lg:max-w-[744px] lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto lg:left-[calc(50%-175px)] lg:-translate-x-1/2' : 'left-0'}`}></div>
      
      <div className={`flex flex-row items-center justify-between bg-white rounded-[12px] xl:rounded-[16px] px-[16px] lg:px-[32px] py-[8px] lg:py-[12px] pointer-events-auto shadow-md transition-all duration-300 ${isSubBusinessPage && isBusiness ? 'max-w-[1100px] mx-auto' : 'mx-[16px] xs:mx-[32px] xl:mx-[64px] 2xl:max-w-[1360px] 2xl:mx-auto'}`}>
        
        {/* Branding */}
        <Link to={isBusiness ? "/business/home" : "/"} className='flex items-center gap-4'>
          <img className='w-[25px] h-[25px]' src={finalDARkIcon} alt="Logo" />
          <div className='flex flex-col'>
            <span className='text-[#323232] text-[14px] xl:text-2xl font-staat tracking-widest uppercase leading-none'>sitemanifest</span>
            {isBusiness && <span className='text-[10px] text-[#323232] font-staat tracking-widest mt-0.5 '>BUSINESS</span>}
          </div>
        </Link>

<div className='hidden lg:flex items-center gap-6'>
  {!loading && (
    <div className='flex items-center gap-2 font-staat text-lg'>
      {!user ? (
        <div className='flex gap-4'>
          <span onClick={() => navigate('/signin')} className='cursor-pointer text-[#323232] hover:opacity-80 transition-opacity'>
            {translations[selectedLanguage]?.signIn}
          </span>
          <span onClick={() => navigate('/signup')} className='cursor-pointer text-[#323232] hover:opacity-80 transition-opacity'>
            {translations[selectedLanguage]?.signUp}
          </span>
        </div>
      ) : (
        /* The container now has a stable height and relative positioning */
        <div className="relative group flex items-center h-[50px] px-2 cursor-pointer">
          
          <div className={`${isBusiness === true ? "hidden" : "flex"} items-center gap-3`}>
            {/* Avatar Circle */}
            <div className={`w-9 h-9  rounded-full overflow-hidden flex-shrink-0 flex items-center ${userRow?.img ? "" : "border border-black/5"} justify-center text-[11px]  text-white`} style={{ backgroundColor: avatarBg }}>
              {userRow?.img ? (
                <img src={userRow.img} className='w-full h-full object-cover' alt="Avatar" />
              ) : (
                fullname[0]?.toUpperCase() || 'U'
              )}
            </div>
            
            {/* Name */}
            <span className='max-w-[120px] truncate text-[#323232] lowercase font-staat tracking-wider'>
              {userRow?.fullname}
            </span>
          </div>
          <div className={`${isBusiness === true ? "flex" : "hidden"} items-center gap-3`}>
            {/* Avatar Circle */}
            <div className={`w-9 h-9  rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center text-[11px] ${isBusiness === true && business?.img ? '' : "border border-black/5"} text-white`} style={{ backgroundColor: avatarBg }}>
              {isBusiness === true && business.img ? (
                <img src={business?.img} className='w-full h-full object-cover' alt="Avatar" />
              ) : (
                business?.title[0]?.toUpperCase() || 'U'
              )}
            </div>
            
            {/* Name */}
            <span className='max-w-[120px] truncate text-[#323232] lowercase font-staat tracking-wider'>
              {business?.title}
            </span>
          </div>
          
          {/* DROPDOWN - Clean White Box 
             - 'top-full' ensures it starts exactly at the bottom of the hover zone
             - 'pt-2' creates a small invisible gap-filler so hover doesn't break
          */}
          <div className="absolute top-full right-0 pt-2 hidden group-hover:block z-[100]">
            <div className="bg-white w-44 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100 rounded-lg overflow-hidden flex flex-col">
              
              {!isBusiness && (
                <>
                  {isAdmin && (
                    <button onClick={() => navigate('/admin/reports')} className="p-4 text-left text-[11px] hover:bg-gray-50 text-[#323232] border-b border-gray-50 font-staat uppercase tracking-widest">
                      Admin
                    </button>
                  )}
                  <button onClick={() => navigate(`/users/${user.id}`)} className="p-4 text-left text-[13px] text-[#323232] hover:bg-gray-50 border-b border-gray-50 font-staat lowercase">
                    my reviews
                  </button>
                  <button onClick={() => navigate('/settings')} className="p-4 text-left text-[13px] text-[#323232] hover:bg-gray-50 border-b border-gray-50 font-staat lowercase">
                    settings
                  </button>
                </>
              )}

              {/* Logout - Professional Muted Style */}
              <button 
                onClick={handleLogout} 
                className="p-4 text-left text-[13px] text-gray-500 hover:text-black hover:bg-gray-50 transition-colors font-staat lowercase"
              >
                log out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )}
  
  {!isBusiness && (
    <button onClick={() => navigate('/business')} className='bg-[#08696D] border-2 border-[#08696D] text-white px-6 py-2 rounded font-staat ml-4 hover:bg-white hover:text-[#08696D] active:scale-95 transition-all duration-200'>
      For Businesses
    </button>
  )}
</div>

        {/* Mobile Toggle */}
        <div className='lg:hidden flex items-center gap-4'>
          {!isBusiness && <img onClick={() => setIsSearch(true)} className="w-6 h-6" src={searchbaricon} alt="search" />}
          <div onClick={() => setIsOpen(!isOpen)} className='flex flex-col gap-1.5 cursor-pointer p-2'>
            <div className={`w-6 h-0.5 bg-[#323232] transition-all ${isOpen ? 'rotate-45 translate-y-2' : ''}`}></div>
            <div className={`w-6 h-0.5 bg-[#323232] ${isOpen ? 'opacity-0' : ''}`}></div>
            <div className={`w-6 h-0.5 bg-[#323232] transition-all ${isOpen ? '-rotate-45 -translate-y-2' : ''}`}></div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && !isBusiness && (
        <div className="lg:hidden absolute  top-full left-3 right-3 bg-white mt-2 rounded-xl p-6 flex flex-col gap-4 text-[#1C6065] font-staat text-xl pointer-events-auto shadow-2xl">
            {user ? (
              <>
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs" style={{backgroundColor: avatarBg}}>
                        {fullname[0]?.toUpperCase()}
                    </div>
                    <span className="text-lg">{fullname}</span>
                </div>
                <span onClick={() => {navigate('/settings'); setIsOpen(false)}}>Settings</span>
                <span onClick={() => {handleLogout(); setIsOpen(false)}} className="text-red-400">Log Out</span>
              </>
            ) : (
              <>
                <span className="cursor-pointer" onClick={() => {navigate('/signin'); setIsOpen(false)}}>Log In</span>
                <span className="cursor-pointer" onClick={() => {navigate('/signup'); setIsOpen(false)}}>Sign Up</span>
              </>
            )}
            <button onClick={() => navigate('/business')} className='bg-[#08696D] text-white px-6 py-3 rounded font-staat'>For Businesses</button>
        </div>
      )}
      {isOpen && isBusiness && (
        <div className="lg:hidden absolute  top-full left-3 right-3 bg-white mt-2 rounded-xl p-6 flex flex-col gap-6 text-[#1C6065] font-staat text-xl pointer-events-auto shadow-2xl">
            {user ? (
              <>
        
              <div onClick={() => {navigate('/business/home'); setIsOpen(false)}} className='flex flex-row items-center gap-[20px]'>
                <div 
  className="w-[24px] h-[24px] bg-[#1C6065]" 
  style={{ 
    maskImage: `url(${homesvg})`,
    WebkitMaskImage: `url(${homesvg})`,
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    maskSize: 'contain'
  }}
/>
                <span>Home</span>
              </div>

                <div onClick={() => {navigate('/business/your-page'); setIsOpen(false)}} className='flex flex-row items-center gap-[20px]'>
                      <div 
  className="w-[24px] h-[24px] bg-[#1C6065]" 
  style={{ 
    maskImage: `url(${yourpagesvg})`,
    WebkitMaskImage: `url(${yourpagesvg})`,
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    maskSize: 'contain'
  }}
/>
    
                <span >Your Page</span>
              </div>

                <div onClick={() => {navigate('/business/business-intel'); setIsOpen(false)}} className='flex flex-row items-center gap-[20px]'>
                       <div 
  className="w-[24px] h-[24px] bg-[#1C6065]" 
  style={{ 
    maskImage: `url(${intelsvg})`,
    WebkitMaskImage: `url(${intelsvg})`,
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    maskSize: 'contain'
  }}
/>
    
                <span >Intel Reports</span>
              </div>
                <div onClick={() => {navigate('/business/analytics'); setIsOpen(false)}} className='flex flex-row items-center gap-[20px]'>
                       <div 
  className="w-[24px] h-[24px] bg-[#1C6065]" 
  style={{ 
    maskImage: `url(${analyticsSvg})`,
    WebkitMaskImage: `url(${analyticsSvg})`,
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    maskSize: 'contain'
  }}
/>
                <span >Analytics</span>
              </div>
                <div onClick={() => {navigate('/business/widget'); setIsOpen(false)}} className='flex flex-row items-center gap-[20px]'>
                       <div 
  className="w-[24px] h-[24px] bg-[#1C6065]" 
  style={{ 
    maskImage: `url(${widgetsvg})`,
    WebkitMaskImage: `url(${widgetsvg})`,
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    maskSize: 'contain'
  }}
/>
                <span >Widgets</span>
              </div>

                <div onClick={() => {navigate('/business/pricing'); setIsOpen(false)}} className='flex flex-row items-center gap-[20px]'>
                       <div 
  className="w-[24px] h-[24px] bg-[#1C6065]" 
  style={{ 
    maskImage: `url(${pricingsvg})`,
    WebkitMaskImage: `url(${pricingsvg})`,
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    maskSize: 'contain'
  }}
/>
                <span >Pricing</span>
              </div>
                <div  onClick={() => {navigate('/business/faqs'); setIsOpen(false)}} className='flex flex-row items-center gap-[20px]'>
                       <div 
  className="w-[24px] h-[24px] bg-[#1C6065]" 
  style={{ 
    maskImage: `url(${faqssvg})`,
    WebkitMaskImage: `url(${faqssvg})`,
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    maskSize: 'contain'
  }}
/>
                <span>Faqs</span>
              </div>
                <div onClick={() => {navigate('/business/settings'); setIsOpen(false)}} className='flex flex-row items-center gap-[20px]'>
                       <div 
  className="w-[24px] h-[24px] bg-[#1C6065]" 
  style={{ 
    maskImage: `url(${settingsSvg})`,
    WebkitMaskImage: `url(${settingsSvg})`,
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    maskSize: 'contain'
  }}
/>
                <span >Settings</span>
              </div>
                <span onClick={() => {handleLogout(); setIsOpen(false)}} className="text-red-400">Log Out</span>
              </>
            ) : (
              <>
                <span className="cursor-pointer" onClick={() => {navigate('/signin'); setIsOpen(false)}}>Log In</span>
                <span className="cursor-pointer" onClick={() => {navigate('/signup'); setIsOpen(false)}}>Sign Up</span>
              </>
            )}
        </div>
      )}
    </header>
  );
}